import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { db } from './firebase';
import { collection, setDoc, doc, getDocs, deleteDoc } from 'firebase/firestore';
import { auth } from './auth';

export default function ContractorImportPage() {
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSCUser, setIsSCUser] = useState(false);

  // Load contractors from Firestore on mount or after import
  async function loadContractorsFromDB() {
    setLoading(true);
    try {
      const contractorCol = collection(db, 'contractors');
      const snapshot = await getDocs(contractorCol);
      const dbRows = [];
      let dbColumns = [];
      // Try to get column order from the first doc's _columnOrder field if present
      let columnOrder = null;
      snapshot.forEach(docSnap => {
        const data = docSnap.data();
        if (data._columnOrder && Array.isArray(data._columnOrder)) {
          columnOrder = data._columnOrder;
        }
      });
      // If no explicit order, collect all unique columns
      if (!columnOrder) {
        snapshot.forEach(docSnap => {
          const data = docSnap.data();
          Object.keys(data).forEach(col => {
            if (col !== '_columnOrder' && !dbColumns.includes(col)) dbColumns.push(col);
          });
        });
        columnOrder = dbColumns;
      }
      // Build rows in correct column order
      snapshot.forEach(docSnap => {
        const data = docSnap.data();
        dbRows.push(columnOrder.map(col => data[col] || ''));
      });
      // Sort rows alphabetically by 'Contractor Name' column if present
      let sortedRows = dbRows;
      const contractNameIdx = columnOrder.findIndex(col => col.trim().toLowerCase() === 'contractor name');
      if (contractNameIdx !== -1) {
        sortedRows = [...dbRows].sort((a, b) => {
          const aName = (a[contractNameIdx] || '').toLowerCase();
          const bName = (b[contractNameIdx] || '').toLowerCase();
          return aName.localeCompare(bName);
        });
      }
      setColumns(columnOrder);
      setRows(sortedRows);
      setError('');
    } catch (err) {
      setError('Failed to load contractors from database.');
    }
    setLoading(false);
  }

  useEffect(() => {
    loadContractorsFromDB();
  }, []);

  useEffect(() => {
    // Check if signed-in user is in SC User List
    const unsubscribe = auth.onAuthStateChanged(async user => {
      if (user && user.email) {
        const usersSnapshot = await getDocs(collection(db, 'users'));
        let found = false;
        usersSnapshot.forEach(doc => {
          const data = doc.data();
          if (data.email && data.email.toLowerCase() === user.email.toLowerCase()) {
            found = true;
          }
        });
        setIsSCUser(found);
      } else {
        setIsSCUser(false);
      }
    });
    return () => unsubscribe();
  }, []);

  async function handleFileChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    setLoading(true);
    // Blank out table immediately
    setRows([]);
    setColumns([]);
    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const json = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        if (json.length === 0) throw new Error('No data found');
        const filteredRows = json.slice(1).filter(row => Array.isArray(row) && row.some(cell => cell !== undefined && cell !== null && String(cell).trim() !== ''));
        const newColumns = json[0];
        // Clean up DB and save new data
        const contractorCol = collection(db, 'contractors');
        const snapshot = await getDocs(contractorCol);
        for (const docSnap of snapshot.docs) {
          await deleteDoc(doc(contractorCol, docSnap.id));
        }
        // Save column order in a hidden field in each doc
        for (let i = 0; i < filteredRows.length; i++) {
          const rowArr = filteredRows[i];
          if (!rowArr || !rowArr.some(cell => cell && String(cell).trim() !== '')) continue;
          const rowObj = {};
          newColumns.forEach((col, j) => {
            rowObj[col] = rowArr[j] || '';
          });
          rowObj._columnOrder = newColumns;
          await setDoc(doc(contractorCol), rowObj);
        }
        // Reload from DB to display
        await loadContractorsFromDB();
        setError('');
      } catch (err) {
        setError('Failed to import and save Excel file.');
      }
      setLoading(false);
    };
    reader.readAsArrayBuffer(file);
  }

  function handleImportClick() {
    if (!isSCUser) {
      alert('Only SC Users are allowed!');
      return;
    }
    document.getElementById('excel-input').click();
  }

  return (
    <div style={{ padding: 32 }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Contractor List</h2>
        <input
          type="text"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          placeholder="Search contractors..."
          style={{ marginLeft: 24, padding: '7px 14px', borderRadius: 5, border: '1px solid #bfc8d6', fontSize: '1.05em', outline: 'none', boxShadow: '0 1px 4px rgba(25,118,210,0.07)', minWidth: 220 }}
        />
        <button onClick={handleImportClick} style={{ marginLeft: 'auto', marginRight: 8 }}>Import from Excel</button>
      </div>
      <input
        id="excel-input"
        type="file"
        accept=".xlsx,.xls"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
      {error && <div style={{ color: 'red', marginBottom: 16 }}>{error}</div>}
      {loading && <div style={{ color: '#1976d2', marginBottom: 16 }}>Loading...</div>}
      {rows.length > 0 && (
        <div style={{overflowX: 'auto'}}>
          <table style={{ width: '95%', minWidth: 800, margin: '0 auto', borderCollapse: 'collapse', background: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.07)', borderRadius: 8 }}>
            <thead style={{ background: '#f5f7fa' }}>
              <tr>
                {columns.map((col, i) => {
                  const colName = col.trim().toLowerCase();
                  const centerCols = ['contract', 'monthly', 'quarterly', 'half yearly', 'yearly'];
                  const alignCenter = centerCols.includes(colName);
                  return (
                    <th key={i} style={{ border: '1px solid #e0e0e0', padding: '10px 14px', color: '#1976d2', fontWeight: 600, fontSize: '1.05em', textAlign: alignCenter ? 'center' : 'left', background: '#f5f7fa' }}>{col}</th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {rows
                .filter(row =>
                  searchTerm.trim() === '' ||
                  row.some(cell =>
                    typeof cell === 'string' && cell.toLowerCase().includes(searchTerm.trim().toLowerCase())
                  )
                )
                .map((row, i) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? '#fafbfc' : '#fff' }}>
                    {columns.map((col, j) => {
                      let value = row[j];
                      const colName = col.trim().toLowerCase();
                      // Format as currency for specific columns
                      if (
                        ['monthly', 'quarterly', 'half yearly', 'yearly'].includes(colName) &&
                        value !== '' &&
                        !isNaN(Number(value))
                      ) {
                        value = `$${Number(value).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
                      }
                      const centerCols = ['contract', 'monthly', 'quarterly', 'half yearly', 'yearly'];
                      const alignCenter = centerCols.includes(colName);
                      return (
                        <td key={j} style={{ border: '1px solid #e0e0e0', padding: '9px 14px', fontSize: '1em', textAlign: alignCenter ? 'center' : 'left', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</td>
                      );
                    })}
                  </tr>
                ))}
            </tbody>
          </table>
          {/* Summary figures below the table */}
          {(() => {
            // Find column indexes
            const monthlyIdx = columns.findIndex(col => col.trim().toLowerCase() === 'monthly');
            const quarterlyIdx = columns.findIndex(col => col.trim().toLowerCase() === 'quarterly');
            const halfYearlyIdx = columns.findIndex(col => col.trim().toLowerCase() === 'half yearly');
            const yearlyIdx = columns.findIndex(col => col.trim().toLowerCase() === 'yearly');
            // Sum values for each column
            let monthlyTotal = 0;
            let quarterlyTotal = 0;
            let halfYearlyTotal = 0;
            let yearlyTotal = 0;
            rows.forEach(row => {
              const mVal = row[monthlyIdx];
              if (monthlyIdx !== -1 && mVal !== '' && !isNaN(Number(mVal))) {
                monthlyTotal += Number(mVal);
              }
              const qVal = row[quarterlyIdx];
              if (quarterlyIdx !== -1 && qVal !== '' && !isNaN(Number(qVal))) {
                quarterlyTotal += Number(qVal);
              }
              const hVal = row[halfYearlyIdx];
              if (halfYearlyIdx !== -1 && hVal !== '' && !isNaN(Number(hVal))) {
                halfYearlyTotal += Number(hVal);
              }
              const yVal = row[yearlyIdx];
              if (yearlyIdx !== -1 && yVal !== '' && !isNaN(Number(yVal))) {
                yearlyTotal += Number(yVal);
              }
            });
            return (
              <div style={{marginTop: '2rem', marginRight: '3rem', textAlign: 'right', fontWeight: 600, fontSize: '1.15em', color: '#1976d2'}}>
                <div>Column Monthly Total: $ {monthlyTotal.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
                <div>Column Quarterly Total: $ {quarterlyTotal.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
                <div>Column Half Yearly Total: $ {halfYearlyTotal.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
                <div>Column Yearly Total: $ {yearlyTotal.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
                <div style={{marginTop: '2rem', color: 'red'}}>Running Quarterly Total: $ {(3 * monthlyTotal + quarterlyTotal).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
                <div style={{color: 'red'}}>Running Half Yearly Total: $ {(6 * monthlyTotal + 2 * quarterlyTotal + halfYearlyTotal).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
                <div style={{color: 'red'}}>Running Yearly Total: $ {(12 * monthlyTotal + 4 * quarterlyTotal + 2 * halfYearlyTotal + yearlyTotal).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
                <div style={{marginTop: '1.5rem', fontWeight: 600, fontSize: '1.12em', color: '#333'}}>
                  <div>Quarterly Admin Fund: $145,000</div>
                  <div>Yearly Admin Fund: $580,000</div>
                  <div>Quarterly CW Fund: $59,000</div>
                  <div>Yearly CW Fund: $236,000</div>
                </div>
              </div>
            );
          })()}
        </div>
      )}
      {!loading && rows.length === 0 && <div>No contractor records found.</div>}
    </div>
  );
}
